import { useState, useMemo, useEffect } from 'react';
import { getVal, setVal } from "oolib";
import { useQueryClient } from 'react-query';
import { useAutoGenFilterConfigsFromTpl } from '@/components/analysisModules/utils/useAutoGenFilterConfigsFromTpl';

/**
 * Custom hook to manage widget-specific filters
 * 
 * @param {Object} params
 * @param {Object} params.widgetConfig - The widget configuration object
 * @param {Function} params.updateWidgetConfig - Function to update the widget config
 * @param {number} params.widgetIndex - Index of the widget being edited
 * @param {Array<string>} params.contentTypesForFilters - Array of content types for generating filters (optional if selectedTpls is provided)
 * @param {Array} params.selectedTpls - Pre-selected templates to use for filters (optional, takes precedence over contentTypesForFilters)
 * @param {string} params.configPath - Path in widget config where filters are stored (default: "props.widgetFilters")
 * @returns {Object} - { filterConfigs, localFilters, handleFilterChange, hasContentTypes, contentTypes }
 */
export const useWidgetFilters = ({
  widgetConfig,
  updateWidgetConfig,
  widgetIndex,
  contentTypesForFilters = [],
  selectedTpls: providedSelectedTpls,
  configPath = "props.widgetFilters"
}) => {
  const queryClient = useQueryClient();
  const allTpls = queryClient.getQueryData("allTpls");

  const [localFilters, setLocalFilters] = useState(
    getVal(widgetConfig, configPath) || []
  );

  useEffect(() => {
    const widgetFilters = getVal(widgetConfig, configPath) || [];
    setLocalFilters(widgetFilters);
  }, [widgetConfig, configPath]);

  const selectedTpls = useMemo(() => {
    // If selectedTpls are provided directly, use them
    if (providedSelectedTpls && providedSelectedTpls.length > 0) {
      return providedSelectedTpls;
    }

    // Otherwise, filter by contentTypesForFilters
    if (!allTpls || !Array.isArray(allTpls)) {
      return [];
    }

    if (!contentTypesForFilters || contentTypesForFilters.length === 0) {
      return [];
    }

    return allTpls.filter(
      tpl =>
        contentTypesForFilters.includes(tpl.kp_content_type) &&
        tpl?.general?.segment === "publishing"
    );
  }, [allTpls, contentTypesForFilters, providedSelectedTpls]);

  // Generate filter configs based on selected templates
  const autoGenFilters = useAutoGenFilterConfigsFromTpl({
    selectedTpls: selectedTpls,
    filterScopes: ["doc", "anno"],
  });

  // Process filter configs (remove "Annotation Filters" section title for cleaner UI)
  const filterConfigs = useMemo(() => {
    if (!autoGenFilters) return null;
    
    return autoGenFilters.map(section => {
      if (section.sectionTitle === 'Annotation Filters') {
        return {
          ...section,
          sectionTitle: undefined
        };
      }
      return section;
    });
  }, [autoGenFilters]);

  const updateConfig = (key, value) => {
    updateWidgetConfig((prev) => {
      let modifiedConfig = JSON.parse(
        JSON.stringify(prev[widgetIndex])
      );

      modifiedConfig = setVal(
        modifiedConfig,
        key,
        value
      );

      const modifiedFullConfig = JSON.parse(JSON.stringify(prev));
      modifiedFullConfig[widgetIndex] = modifiedConfig;
      return modifiedFullConfig;
    });
  };

  const handleFilterChange = (newFiltersOrUpdater) => {
    if (typeof newFiltersOrUpdater === 'function') {
      setLocalFilters(prev => {
        const next = newFiltersOrUpdater(prev);
        updateConfig(configPath, next);
        return next;
      });
    } else {
      setLocalFilters(newFiltersOrUpdater);
      updateConfig(configPath, newFiltersOrUpdater);
    }
  };

  const hasContentTypes = useMemo(() => {
    if (providedSelectedTpls && providedSelectedTpls.length > 0) {
      return true;
    }
    return contentTypesForFilters && contentTypesForFilters.length > 0;
  }, [contentTypesForFilters, providedSelectedTpls]);

  // This is needed for filters like authorsType to query the backend correctly
  const contentTypes = useMemo(() => {
    if (providedSelectedTpls && providedSelectedTpls.length > 0) {
      return providedSelectedTpls.map(tpl => tpl.kp_content_type);
    }
    return contentTypesForFilters || [];
  }, [contentTypesForFilters, providedSelectedTpls]);

  return {
    filterConfigs,
    localFilters,
    handleFilterChange,
    hasContentTypes,
    contentTypes
  };
};

